import { useEffect } from 'react';
import { FormProvider, Controller } from 'react-hook-form';
import Select from 'react-select';
import { ReactComponent as RiAlertLine } from '@/assets/icons/ri-alert-line.svg';
import { ReactComponent as RiBadmintonCourt } from '@/assets/icons/ri-badminton-court.svg';
import Input from '@/components/common/Input';
import { currencyFormat, checkWeekday, groupBookingHours } from '@/utils';


const BOOKING_METHODS = [
  {
    id: 1,
    label: 'Walk in'
  },
  {
    id: 3,
    label: 'Call/Chat'
  },
  // {
  //   id: 2,
  //   label: 'Customer App'
  // }
]


const summaryData = (schedules) => {
  const summary = {}
  const hours = {}
  schedules.forEach(schedule => {
    const court = schedule.court.id
    if (hours[court]) { hours[court].push(schedule.hour) }
    else { hours[court] = [schedule.hour] }
    summary[court] = {
      fieldName: schedule.court.code,
      hours: hours[court],
      id: schedule.court.id,
      date: schedule.selectedDate,
      venuePriceId: schedule.court.venue_price_id
    }
  })
  return summary
}

const formatBookingPrice = (summary, field, selectedBookingType, selectedSchedules, selectedVenue, holiday) => {
  let bookedCourtPrice = 0
  const { venuePriceId, id } = field
  const courtPrices = selectedVenue.prices.find(price => price.id === venuePriceId)?.details
  const court = summary[id]
  const dayType = checkWeekday(court.date)
  const courtPrice = holiday ?
    courtPrices.filter(price => price.day_type === 'HOLIDAY') :
    courtPrices?.filter(price => price.type === selectedBookingType && price.day_type === dayType)
  field.hours?.forEach(bookingTime => {
    courtPrice?.forEach(price => {
      if (bookingTime >= price.start_time && bookingTime <= price.end_time) {
        bookedCourtPrice += price.price
      }
    })
  })
  return currencyFormat(bookedCourtPrice)
}


const calculateLocalTax = (price: number, taxFee: number) => (price * taxFee) / 100 


const Summary = ({ summaryMethods, selectedBookingType, selectedSchedules, conflictingSchedules, selectedVenue, holiday, customer }) => {
  useEffect(() => {
    summaryMethods.setValue('coupon', '')
  }, [selectedVenue])
  let venueCount = 0
  const totalPrice = () => {
    let result = 0
    const details = Object.entries(selectedSchedules)?.map((schedule: {}) => schedule[1]?.map(bookingDetail => {
        const { court, hour, selectedDate, price } = bookingDetail
        return {
          venue_court_id: court.id,
          start_time: hour,
          end_time: hour+1,
          date: selectedDate,
          price
        }
      })
    )
    if (details.flat().length) {
      result = details.flat().map(detail => detail.price)?.reduce((num, total = 0) => total + num) || 0
    }
    return result
  }


  const prevBookingId = summaryMethods.watch("prevBookingId")
  const prevPrice = summaryMethods.watch("prevPrice")
  const prevDiscAmount = summaryMethods.watch("prevDiscAmount")

  const feeStr = summaryMethods.watch('rescheduleFee')
  const rescheduleFee = parseFloat(feeStr === undefined || feeStr === "" ? 0 : feeStr)
  const bookingPriceTotal = totalPrice() - (prevPrice + prevDiscAmount)
  const finalPrice = bookingPriceTotal + rescheduleFee
  let localTax = calculateLocalTax(bookingPriceTotal, selectedVenue?.tax_fee) 
  localTax = localTax > 0 ? localTax : 0
  let totalPayment = finalPrice + localTax 
  totalPayment = totalPayment > 0 ? totalPayment : 0
  let refundPrice = finalPrice < 0 ? finalPrice * -1 : 0
  refundPrice = refundPrice >= prevDiscAmount ? refundPrice - prevDiscAmount : refundPrice

  return (
    <div className='flex flex-col basis-3/12 bg-white p-0'>
      {!!Object.entries(selectedSchedules).length && <div className='flex items-start px-8 pt-8 pb-0 gap-3 rounded-t'>
        <p className='font-bold text-lg'>Summary</p>
      </div>}
      <div className='flex flex-col items-start p-8 gap-6 rounded-b'>
        {!!conflictingSchedules.length &&
				<div className='flex w-full px-3 py-2.5 gap-2 bg-red-subtle border-l-4 border-red-secondary text-red-secondary'>
					<RiAlertLine/>
					<p className='text-sm text-black-primary w-5/6'>There is a conflicting schedule</p>
				</div>}
        {(customer !== null && customer.is_invoice_overdue) &&
				<div className='flex w-full justify-between px-3 py-2.5 gap-2 bg-red-subtle border-l-4 border-red-secondary text-red-secondary'>
					<RiAlertLine/>
					<p className='text-sm text-black-primary w-5/6'>Customer has overdue invoices</p>
				</div>}
        {(customer !== null && customer.is_blocked) &&
				<div className='flex w-full justify-between px-3 py-2.5 gap-2 bg-red-subtle border-l-4 border-red-secondary text-red-secondary'>
					<RiAlertLine/>
					<p className='text-sm text-black-primary w-5/6'>Customer blocked due: {customer.blocked_note}</p>
				</div>}
        {Object.keys(selectedSchedules).map(date => {
            const summary = summaryData(selectedSchedules[date]);
            return (!!Object.keys(summary).length &&
							<div className='flex flex-col w-full gap-4'>
								<div className='flex py-3 gap-2 bg-grey-subtle border-b border-grey-line-subtle'>
									<p className='pl-2 font-medium text-xs'>{`${new Date(Number(date)).toLocaleString('id-ID', {
                    day: 'numeric',
                    year: 'numeric',
                    month: 'long',
                  })}`}</p>
								</div>
                {Object.keys(summary).map(court => {
                  venueCount+=1
                  const field = summary[court];
                  return (
                    <div className='flex px-2 py-0'>
                      <div className='flex justify-between items-center p-0 gap-28'>
                        <div className='flex p-0 gap-4'>
                          <div
                            className='flex flex-col justify-center items-center px-3 py-4 gap-2 bg-green-success rounded text-white'>
                            <RiBadmintonCourt />
                          </div>
                          <div className='flex flex-col justify-center p-0'>
                            <p className='font-medium text-sm'>{`${field.fieldName}`}</p>
                            {Object.values(groupBookingHours(field.hours)).map(hour => {
                              const { start, end } = hour;
                              return (
                                <p className='text-xs text-grey-dark'>({`${start}.00 - ${end}.00`} WITA)</p>
                              );
                            })}
                          </div>
                        </div>
                        <p
                          className='flex justify-end font-bold text-sm'>{formatBookingPrice(summary, field, selectedBookingType, selectedSchedules, selectedVenue, holiday)}</p>
                      </div>
                    </div>
                  );
                })}
							</div>
            );
          },
        )}
      </div>
      <div>
        <form className='w-full'>
          <FormProvider {...summaryMethods}>
            <div className='flex flex-col p-7 gap-7 justify-between w-full'>
              <div className='flex flex-col p-0 gap-2 w-full'>
                <p className='font-medium text-sm'>Booking Method</p>
                <Controller
                  name='bookingMethod'
                  render={({ field }) =>
                    <Select
                      name='bookingMethod'
                      options={
                        BOOKING_METHODS.map(method => ({
                          label: method.label,
                          value: method.id,
                        }))
                      }
                      placeholder='Choose Booking Method'
                      {...summaryMethods.register('bookingMethod')}
                      {...field}
                    />
                  }
                />
              </div>
              <div className='flex flex-col p-0 gap-2 w-full'>
                <p className='font-medium text-sm'>Reschedule Fee</p>
                <Input name='rescheduleFee' type='number' placeholder='Input Reschedule Fee'
                    className='w-full' {...summaryMethods.register('rescheduleFee')} />
              </div>
              <div
                className='flex flex-col justify-between items-center gap-1 p-2 rounded-md border border-grey-line-subtle'>
                <p className='font-bold text-sm w-full'>Details</p>
                <div className='flex w-full justify-between items-center py-2'>
                  <p className='text-xs'>Field Booking ({venueCount})</p>
                  <p className='font-medium text-xs'>{currencyFormat(totalPrice())}</p>
                </div>
                <div className='flex w-full justify-between items-center py-2'>
                  <p className='text-xs'>Paid <span className='text-red-primary'>#{prevBookingId}</span></p>
                  <p className='font-medium text-xs'>- {currencyFormat(prevPrice)}</p>
                </div>
                <div className='flex w-full justify-between items-center py-2 bg-indigo-500'>
                  <p className='text-xs'>Voucher <span className='text-red-primary'>#{prevBookingId}</span></p>
                  <p className='font-medium text-xs'>- {currencyFormat(prevDiscAmount)}</p>
                </div>
                <div className='flex w-full justify-between items-center py-2 bg-indigo-500'>
                  <p className='text-xs'>Local Tax {selectedVenue?.tax_fee}%</p>
                  <p className='font-medium text-xs'>{currencyFormat(localTax)}</p>
                </div>
                <div className='flex w-full justify-between items-center py-2'>
                  <p className='text-xs'>Reschedule Fee</p>
                  <p className='font-medium text-xs'>{currencyFormat(rescheduleFee)}</p>
                </div>
                <div className='w-full border-b border-grey-line-subtle' />
                <div className='flex w-full justify-between items-center py-2'>
                  <p className='text-xs'>Total Payment</p>
                  <p className='font-medium text-xs'>{currencyFormat(totalPayment)}</p>
                </div>
                { refundPrice > 0 && refundPrice !== prevPrice &&
                  <div className='flex w-full justify-between items-center py-2'>
                  <p className='text-xs'>Refund</p>
                  <p className='font-medium text-xs'>{currencyFormat(refundPrice)}</p>
                </div>
                }
                <div className="text-grey-dark text-xs w-full">
                  <small>*Tax from previous booking is non refundable</small>
                </div>
              </div>
            </div>
          </FormProvider>
        </form>
      </div>
    </div>
  );
}
export default Summary
